import React, { useEffect, useRef, useState } from 'react';
import { EqualHeightElement } from 'react-equal-height';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

// Prime-React Components
import { Menu } from 'primereact/menu';
import { Button } from 'primereact/button';
import { Chart } from 'primereact/chart';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import 'chartjs-plugin-datalabels';

// Components
import { CustomeTooltip } from './ActionButtons';

// Redux-Actions
import { getTechnologyWithFilter } from '../redux/actions/admin';

// Constants
import {
  DashboardStatusGraphlabels,
  INTERVIEW_ROUND_STATUS,
  INTERVIEW_BACKGROUND_COLORS_DARK_MODE,
  INTERVIEW_COLORS,
  INTERVIEW_STATUS_LIST,
  STATUS_INTERVIEW
} from '../constants/common';

// Images
import filterIc from '@assets/images/filter-ic.svg';
import filterIcDark from '@assets/images/filter-ic-dark.svg';

// Utils
import { getDateWithDay } from '../utils/date';
import { getBarOptionsForStatus } from '../utils/chart';
import { isDarkMode, wordCapitalize } from '../utils/common';
import DatePicker from 'react-datepicker';

const mapPendingToScheduleStatus = (status) => {
  const pendingToScheduleMap = {
    [STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_PENDING]:
      STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_SCHEDULE,

    [STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_PENDING]:
      STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_SCHEDULE,

    [STATUS_INTERVIEW.PRACTICAL_PENDING]: STATUS_INTERVIEW.PRACTICAL_SCHEDULE,

    [STATUS_INTERVIEW.MANAGEMENT_PENDING]: STATUS_INTERVIEW.MANAGEMENT_SCHEDULE,

    [STATUS_INTERVIEW.HR_PENDING]: STATUS_INTERVIEW.HR_SCHEDULE
  };

  return pendingToScheduleMap[status] ?? status;
};

const PieChart = () => {
  const dispatch = useDispatch();
  const { interviewRoundData, pieChartFilterdData } = useSelector((state) => state.adminDashboard);
  const scheduleInterviewCount = useSelector((state) => state.hrs.scheduleInterviewCount);

  const hrFilterRef = useRef(null);
  const [roundStatusCode, setRoundStatusCode] = useState({});
  const [selectedCount, setSelectedCount] = useState({});
  const [rejectedCount, setRejectedCount] = useState({});
  const [offeredCount, setOfferedCount] = useState({});
  const [inDiscussionCount, setInDiscussionCount] = useState({});
  const [offerRejectedCount, setOfferRejectedCount] = useState({});
  const [offerAcceptedCount, setOfferAcceptedCount] = useState({});
  const [furtherReviewCount, setFurtherReviewCount] = useState({});
  const [onHoldCount, setOnHoldCount] = useState({});
  const [hrData, setHrData] = useState([]);
  const [selectedHR, setSelectedHR] = useState('');
  const [chartData, setChartData] = useState([]);
  const [startDateRange, setStartDateRange] = useState(new Date());
  const [endDateRange, setEndDateRange] = useState(new Date());

  function modifiedHrData(data) {
    const scheduleInterviews = data || [];
    let labels = [
      {
        label: 'All',
        value: '',
        command: () => setSelectedHR(''),
        className: selectedHR === '' ? 'highlighted-item' : ''
      }
    ];

    scheduleInterviews.forEach((hr) => {
      let hrName = `${wordCapitalize(hr?.FirstName || '')} ${wordCapitalize(hr.LastName || '')}`;
      labels.push({
        label: hrName,
        value: hr?.ID,
        command: () => setSelectedHR(hr?.ID),
        className: selectedHR === hr?.ID ? 'highlighted-item' : ''
      });
    });
    setHrData(labels);
  }

  useEffect(() => {
    modifiedHrData(scheduleInterviewCount);
  }, [scheduleInterviewCount]);

  useEffect(() => {
    let data = (interviewRoundData?.length !== 0 ? interviewRoundData : pieChartFilterdData) || [];
    const modifiedInterviewRoundStatus = data.map((item) => {
      return {
        HR: {
          ...item.HROfRound
        },
        Status: item.Status,
        Result: item.Result
      };
    });

    setChartData(modifiedInterviewRoundStatus);
  }, [interviewRoundData, pieChartFilterdData]);

  useEffect(() => {
    const modifiedInterviewRoundStatus = (pieChartFilterdData || []).map((item) => {
      return {
        HR: {
          ...item.HROfRound
        },
        Status: item.Status,
        Result: item.Result
      };
    });

    setChartData(modifiedInterviewRoundStatus);
  }, [pieChartFilterdData]);

  const interviewStatusObject = () => {
    return DashboardStatusGraphlabels.reduce((obj, item) => {
      obj[item] = 0;
      return obj;
    }, {});
  };

  const setDefaultValue = () => {
    return DashboardStatusGraphlabels.reduce((obj, item) => {
      obj[item] = null;
      return obj;
    }, {});
  };

  const getInterviewRoundName = (status) => {
    return INTERVIEW_STATUS_LIST.filter((item) => item.code === status)[0]?.name;
  };

  const generateLabelForChart = () => {
    return DashboardStatusGraphlabels.map((status) => {
      const parts = status.split(' ');
      const first = parts.shift(); // First word
      const last = parts.pop(); // Last word
      const middle = parts.join(' '); // Middle part
      return [first, middle, last].filter((part) => part !== '' && part !== undefined);
    });
  };

  // for interview status
  function filterData(data) {
    if (!data?.length) {
      return { statusData: [] };
    }

    const statusCodeData = interviewStatusObject();
    const rejectedData = setDefaultValue();
    const selectedData = setDefaultValue();
    const offeredData = setDefaultValue();
    const inDiscussionData = setDefaultValue();
    const offerRejectedData = setDefaultValue();
    const offerAcceptedData = setDefaultValue();
    const furtherReviewData = setDefaultValue();
    const onHoldData = setDefaultValue();

    const completeStatuses = [
      STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_COMPLETE,
      STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_COMPLETE,
      STATUS_INTERVIEW.PRACTICAL_COMPLETE,
      STATUS_INTERVIEW.HR_COMPLETE,
      STATUS_INTERVIEW.MANAGEMENT_COMPLETE
    ];

    // Helper to process a single round
    const processRound = (round) => {
      const normalizedStatus = mapPendingToScheduleStatus(round.Status);
      const roundName = getInterviewRoundName(normalizedStatus);
      if (completeStatuses.includes(normalizedStatus)) {
        switch (round.Result.toString()) {
          case INTERVIEW_ROUND_STATUS.Selected:
            selectedData[roundName] = (selectedData[roundName] || 0) + 1;
            break;
          case INTERVIEW_ROUND_STATUS['Not Selected']:
            rejectedData[roundName] = (rejectedData[roundName] || 0) + 1;
            break;
          case INTERVIEW_ROUND_STATUS.Offered:
            offeredData[roundName] = (offeredData[roundName] || 0) + 1;
            break;
          case INTERVIEW_ROUND_STATUS['In Discussion']:
            inDiscussionData[roundName] = (inDiscussionData[roundName] || 0) + 1;
            break;
          case INTERVIEW_ROUND_STATUS['Offer Rejected']:
            offerRejectedData[roundName] = (offerRejectedData[roundName] || 0) + 1;
            break;
          case INTERVIEW_ROUND_STATUS['Offer Accepted']:
            offerAcceptedData[roundName] = (offerAcceptedData[roundName] || 0) + 1;
            break;
          case INTERVIEW_ROUND_STATUS['Further Review']:
            furtherReviewData[roundName] = (furtherReviewData[roundName] || 0) + 1;
            break;
          case INTERVIEW_ROUND_STATUS['On Hold']:
            onHoldData[roundName] = (onHoldData[roundName] || 0) + 1;
            break;
          default:
            inDiscussionCount[roundName] = (inDiscussionCount[roundName] || 0) + 1;
        }
      } else {
        statusCodeData[roundName] = (statusCodeData[roundName] || 0) + 1;
      }
    };

    data.forEach((round) => {
      // Only process rounds matching the selectedHR if one is specified
      if (!selectedHR || (selectedHR && selectedHR === round?.HR?.ID)) {
        processRound(round);
      }
    });

    setRoundStatusCode(statusCodeData);
    setSelectedCount(selectedData);
    setRejectedCount(rejectedData);
    setOfferedCount(offeredData);
    setInDiscussionCount(inDiscussionData);
    setOfferRejectedCount(offerRejectedData);
    setOfferAcceptedCount(offerAcceptedData);
    setFurtherReviewCount(furtherReviewData);
    setOnHoldCount(onHoldData);
  }

  useEffect(() => {
    filterData(chartData);
    modifiedHrData(scheduleInterviewCount);
  }, [interviewRoundData, chartData, selectedHR]);

  useEffect(() => {
    filterData(chartData);
    modifiedHrData(scheduleInterviewCount);
  }, [pieChartFilterdData, chartData, selectedHR]);

  useEffect(() => {
    if (startDateRange && endDateRange) {
      const formattedStartDate = getDateWithDay(moment(startDateRange).startOf('day').toDate());
      const formattedEndDate = getDateWithDay(moment(endDateRange).endOf('day').toDate());

      dispatch(getTechnologyWithFilter(formattedStartDate, formattedEndDate));
    }
  }, [dispatch, startDateRange, endDateRange]);

  const darkModeColors = [
    // INTERVIEW_BACKGROUND_COLORS_DARK_MODE.PENDING,
    INTERVIEW_BACKGROUND_COLORS_DARK_MODE.SCHEDULE,
    // INTERVIEW_BACKGROUND_COLORS_DARK_MODE.RESCHEDULE,
    INTERVIEW_BACKGROUND_COLORS_DARK_MODE.COMPLETE,
    // INTERVIEW_BACKGROUND_COLORS_DARK_MODE.PENDING,
    INTERVIEW_BACKGROUND_COLORS_DARK_MODE.SCHEDULE,
    // INTERVIEW_BACKGROUND_COLORS_DARK_MODE.RESCHEDULE,
    INTERVIEW_BACKGROUND_COLORS_DARK_MODE.COMPLETE,
    // INTERVIEW_BACKGROUND_COLORS_DARK_MODE.PENDING,
    INTERVIEW_BACKGROUND_COLORS_DARK_MODE.SCHEDULE,
    // INTERVIEW_BACKGROUND_COLORS_DARK_MODE.RESCHEDULE,
    INTERVIEW_BACKGROUND_COLORS_DARK_MODE.COMPLETE,
    // INTERVIEW_BACKGROUND_COLORS_DARK_MODE.PENDING,
    INTERVIEW_BACKGROUND_COLORS_DARK_MODE.SCHEDULE,
    // INTERVIEW_BACKGROUND_COLORS_DARK_MODE.RESCHEDULE,
    INTERVIEW_BACKGROUND_COLORS_DARK_MODE.COMPLETE,
    // INTERVIEW_BACKGROUND_COLORS_DARK_MODE.PENDING,
    INTERVIEW_BACKGROUND_COLORS_DARK_MODE.SCHEDULE,
    // INTERVIEW_BACKGROUND_COLORS_DARK_MODE.RESCHEDULE,
    INTERVIEW_BACKGROUND_COLORS_DARK_MODE.COMPLETE,
    INTERVIEW_BACKGROUND_COLORS_DARK_MODE.CANCEL
  ];

  const lightModeColors = [
    // INTERVIEW_COLORS.PENDING,
    INTERVIEW_COLORS.SCHEDULE,
    // INTERVIEW_COLORS.RESCHEDULE,
    INTERVIEW_COLORS.COMPLETE,
    // INTERVIEW_COLORS.PENDING,
    INTERVIEW_COLORS.SCHEDULE,
    // INTERVIEW_COLORS.RESCHEDULE,
    INTERVIEW_COLORS.COMPLETE,
    // INTERVIEW_COLORS.PENDING,
    INTERVIEW_COLORS.SCHEDULE,
    // INTERVIEW_COLORS.RESCHEDULE,
    INTERVIEW_COLORS.COMPLETE,
    // INTERVIEW_COLORS.PENDING,
    INTERVIEW_COLORS.SCHEDULE,
    // INTERVIEW_COLORS.RESCHEDULE,
    INTERVIEW_COLORS.COMPLETE,
    // INTERVIEW_COLORS.PENDING,
    INTERVIEW_COLORS.SCHEDULE,
    // INTERVIEW_COLORS.RESCHEDULE,
    INTERVIEW_COLORS.COMPLETE,
    INTERVIEW_COLORS.CANCEL
  ];

  const statusChartData = {
    labels: generateLabelForChart(),
    datasets: [
      {
        data: DashboardStatusGraphlabels.map((status) => roundStatusCode[status]),
        borderWidth: 0,
        borderRadius: 50,
        barThickness: 20,
        backgroundColor: isDarkMode() ? darkModeColors : lightModeColors,
        datalabels: {
          color: '#FFF'
        }
      },
      {
        borderWidth: 0,
        label: 'Selected',
        borderRadius: 50,
        barThickness: 20,
        backgroundColor: INTERVIEW_COLORS.COMPLETE,
        datalabels: {
          color: '#FFF'
        },
        data: DashboardStatusGraphlabels.map((status) => selectedCount[status])
      },
      {
        borderWidth: 0,
        label: 'Rejected',
        borderRadius: 50,
        barThickness: 20,
        backgroundColor: INTERVIEW_COLORS.CANCEL,
        datalabels: {
          color: '#FFF'
        },
        data: DashboardStatusGraphlabels.map((status) => rejectedCount[status])
      },
      {
        borderWidth: 0,
        label: 'Offered',
        borderRadius: 50,
        barThickness: 20,
        backgroundColor: INTERVIEW_COLORS.COMPLETE,
        datalabels: {
          color: '#FFF'
        },
        data: DashboardStatusGraphlabels.map((status) => offeredCount[status])
      },
      {
        borderWidth: 0,
        label: 'In Discussion',
        borderRadius: 50,
        barThickness: 20,
        backgroundColor: INTERVIEW_COLORS.RESCHEDULE,
        datalabels: {
          color: '#FFF'
        },
        data: DashboardStatusGraphlabels.map((status) => inDiscussionCount[status])
      },
      {
        borderWidth: 0,
        label: 'Offer Rejected',
        borderRadius: 50,
        barThickness: 20,
        backgroundColor: INTERVIEW_COLORS.CANCEL,
        datalabels: {
          color: '#FFF'
        },
        data: DashboardStatusGraphlabels.map((status) => offerRejectedCount[status])
      },
      {
        borderWidth: 0,
        label: 'Offer Accepted',
        borderRadius: 50,
        barThickness: 20,
        backgroundColor: INTERVIEW_COLORS.COMPLETE,
        datalabels: {
          color: '#FFF'
        },
        data: DashboardStatusGraphlabels.map((status) => offerAcceptedCount[status])
      },
      {
        borderWidth: 0,
        label: 'Further Review',
        borderRadius: 50,
        barThickness: 20,
        backgroundColor: INTERVIEW_COLORS.SCHEDULE,
        datalabels: {
          color: '#FFF'
        },
        data: DashboardStatusGraphlabels.map((status) => furtherReviewCount[status])
      },
      {
        borderWidth: 0,
        label: 'On Hold',
        borderRadius: 50,
        barThickness: 20,
        backgroundColor: INTERVIEW_COLORS.PENDING,
        datalabels: {
          color: '#FFF'
        },
        data: DashboardStatusGraphlabels.map((status) => onHoldCount[status])
      }
    ]
  };

  return (
    <div className="white-box-item full-width">
      <div className="white-box">
        <div className="card-title-block card-title-inner-wrapper">
          <EqualHeightElement name="title">
            <div className="card-title-wrapper">
              <span className="card-title">Interview status</span>
              <i
                className="custom-target-icon pi pi-info-circle pl-1"
                data-tip={`Interviews based on Status`}
                style={{ cursor: 'pointer' }}
              />
            </div>
          </EqualHeightElement>
          <div className="filter-block">
            <Menu
              model={hrData}
              popup
              ref={hrFilterRef}
              id="popup_menu"
              appendTo="self"
              className="popup_menu action-popup chartFilterMenu hr-name-list"
            />
            <CustomeTooltip />
            <div className="space-between datepicker-btn-outer">
              <div className="flex align-items-center justify-content-between datepicker-outer">
                <span className="p-float-label w-full mr-2 datepicker-wrapper">
                  <DatePicker
                    id="startDate"
                    selected={startDateRange}
                    onChange={(date) => {
                      setStartDateRange(date);
                      if (moment(date).isAfter(endDateRange)) {
                        setEndDateRange(date);
                      }
                    }}
                    maxDate={new Date()}
                    onKeyDown={(e) => e.preventDefault()}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Date"
                    className="p-inputtext w-full custom-datepicker"
                  />
                  <label htmlFor="startDate">Start Date</label>
                </span>
                <span className="p-float-label w-full mr-2 datepicker-wrapper datepicker-bottom-end">
                  <DatePicker
                    id="endDate"
                    selected={endDateRange}
                    onChange={(date) => setEndDateRange(date)}
                    minDate={startDateRange}
                    maxDate={new Date()}
                    onKeyDown={(e) => e.preventDefault()}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Date"
                    className="p-inputtext w-full custom-datepicker"
                  />
                  <label htmlFor="endDate">End Date</label>
                </span>
              </div>
              <Button
                data-tip="Filter By HR"
                type="button"
                onClick={(event) => hrFilterRef.current.toggle(event)}
                aria-controls="popup_menu"
                aria-haspopup
                className="p-button-rounded p-button-text border-none primary-icon-btn filter-btn">
                <i>
                  <img src={filterIc} className="default-img" alt="filter" />
                  <img src={filterIcDark} className="dark-mode-img" alt="filter" />
                </i>
              </Button>
            </div>
          </div>
        </div>

        <div className="chart-container">
          <div className="chart-figure-wrapper">
            <Chart
              type="bar"
              data={statusChartData}
              options={getBarOptionsForStatus()}
              plugins={[ChartDataLabels]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PieChart;
